"use client";

import { useMemo } from "react";
import { useSpotUser } from "@/utils/spotUser";
import { DATA_KEYS, useRemoteData } from "@/utils/useRemoteData";
import type { IntegrationsInfoResponse } from "@/generated";
import { useEditorReadOnly } from "@/hooks/useEditorReadOnly";

export interface MCPToolInfo {
  name: string;
  description?: string | null;
}

export interface MCPCredentialRow {
  id: string;
  display_name: string;
  status: string | null;
  integration_type: string;
  integration_name: string;
  integration_image: string;
  catalog_key: string | null;
  server_url: string | null;
  discovered_tools: MCPToolInfo[];
  enabled_tools: string[];
}

export interface MCPIntegrationRow {
  prefix: string;
  image: string;
  integrationName: string;
}

const MCP_TYPE_PREFIX = "mcp_";

/** Catalog-backed MCP integrations keyed by their runtime tool prefix. Unlike
 *  {@link collectMcpCredentials} these need no connected credential — every
 *  catalog entry (including the in-process ``mcp_noxus`` server) carries its
 *  icon via ``integration.image``, sourced from the backend MCP catalog. */
export function collectMcpIntegrations(
  data: IntegrationsInfoResponse | undefined
): MCPIntegrationRow[] {
  if (!data) return [];
  const rows: MCPIntegrationRow[] = [];
  for (const integration of data.integrations) {
    if (integration.category !== "MCP") continue;
    if (!integration.type.startsWith(MCP_TYPE_PREFIX)) continue;
    const prefix = integration.type.slice(MCP_TYPE_PREFIX.length);
    // ``mcp_custom`` has no stable runtime prefix — custom servers are
    // namespaced ``custom_<hash>`` and only ever exist as credentials.
    if (!prefix || prefix === "custom") continue;
    rows.push({
      prefix,
      image: integration.image,
      integrationName: integration.display_name,
    });
  }
  return rows;
}

export function collectMcpCredentials(
  data: IntegrationsInfoResponse | undefined
): MCPCredentialRow[] {
  if (!data) return [];
  const rows: MCPCredentialRow[] = [];
  for (const integration of data.integrations) {
    if (integration.category !== "MCP") continue;
    for (const provider of integration.providers) {
      for (const cred of provider.credentials ?? []) {
        const meta = (cred.meta ?? {}) as Record<string, unknown>;
        const mcp = (meta["mcp"] ?? {}) as {
          catalog_key?: string;
          server_url?: string;
          discovered_tools?: MCPToolInfo[];
          disabled_tools?: string[];
          enabled_tools?: string[];
        };
        const discovered = mcp.discovered_tools ?? [];
        // Storage is deny-based; legacy rows still carry an allowlist.
        const enabled =
          mcp.disabled_tools !== undefined
            ? discovered
                .map((t) => t.name)
                .filter((name) => !mcp.disabled_tools?.includes(name))
            : (mcp.enabled_tools ?? []);
        rows.push({
          id: cred.id,
          display_name: cred.display_name ?? integration.display_name,
          status: cred.status ?? null,
          integration_type: integration.type,
          integration_name: integration.display_name,
          integration_image: integration.image,
          catalog_key: mcp.catalog_key ?? null,
          server_url: mcp.server_url ?? null,
          discovered_tools: discovered,
          enabled_tools: enabled,
        });
      }
    }
  }
  return rows;
}

/** Single source of truth for the per-group MCP credential list. Both
 *  `MCPCredentialField` and `MCPCredentialPickerField` use this so they
 *  share a react-query cache entry instead of double-fetching. */
export function useMCPCredentials() {
  const { spotGroup } = useSpotUser();
  // In editor view-only mode the integrations:edit-gated config endpoint
  // would 403, and the MCP credential dropdown is disabled anyway.
  const isReadOnly = useEditorReadOnly();
  const result = useRemoteData(
    DATA_KEYS.INTEGRATIONS_CONFIG,
    { path: { group_id: spotGroup.id } },
    { enabled: !isReadOnly }
  );
  const data = result.data as IntegrationsInfoResponse | undefined;
  const credentials = useMemo(() => collectMcpCredentials(data), [data]);
  const mcpIntegrations = useMemo(() => collectMcpIntegrations(data), [data]);
  return {
    credentials,
    mcpIntegrations,
    isLoading: result.isLoading,
    catalogLoaded: !isReadOnly && result.isSuccess,
  };
}
